import React from 'react';
import { ActivitySquare, Scale, Ruler, User } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров WHR
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет текущих значений
  const waist = parseFloat(formData.waist) || 0;
  const hip = parseFloat(formData.hip) || 0;
  const age = parseFloat(formData.age) || 0;
  
  // Выбор пола
  const handleGenderChange = (gender) => {
    setFormData(prev => {
      // Корректируем значения для выбранного пола
      let newWaist, newHip;
      
      if (gender === 'female') {
        newWaist = formData.measurementSystem === 'metric' ? '70' : '28';
        newHip = formData.measurementSystem === 'metric' ? '100' : '39';
      } else {
        newWaist = formData.measurementSystem === 'metric' ? '85' : '33';
        newHip = formData.measurementSystem === 'metric' ? '95' : '37';
      }
      
      return {
        ...prev,
        gender,
        waist: newWaist,
        hip: newHip
      };
    });
  };
  
  // Обработчик изменения талии
  const handleWaistChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      waist: newValue
    }));
  };
  
  // Обработчик изменения бедер
  const handleHipChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      hip: newValue
    }));
  };
  
  // Обработчик изменения возраста
  const handleAgeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      age: newValue
    }));
  };
  
  // Расчет текущего WHR для отображения
  const currentWHR = (waist && hip) ? (waist / hip).toFixed(2) : '-';
  
  // Предопределенные типы телосложения для быстрого выбора
  const presetBodyTypes = [
    { 
      id: 'slender', 
      name: 'Стройный', 
      femaleParams: { waist: '60', hip: '90' },
      maleParams: { waist: '75', hip: '90' }
    },
    { 
      id: 'average', 
      name: 'Средний', 
      femaleParams: { waist: '70', hip: '100' },
      maleParams: { waist: '85', hip: '95' }
    },
    { 
      id: 'athletic', 
      name: 'Атлетичный', 
      femaleParams: { waist: '65', hip: '95' },
      maleParams: { waist: '80', hip: '95' }
    },
    { 
      id: 'full', 
      name: 'Полный', 
      femaleParams: { waist: '85', hip: '110' },
      maleParams: { waist: '95', hip: '100' }
    }
  ];
  
  // Обработчик выбора предустановленного типа телосложения
  const handleBodyTypeSelect = (typeId) => {
    const bodyType = presetBodyTypes.find(type => type.id === typeId);
    if (!bodyType) return;
    
    const params = formData.gender === 'female' ? bodyType.femaleParams : bodyType.maleParams;
    
    // Если имперская система, конвертируем значения
    if (formData.measurementSystem === 'imperial') {
      setFormData(prev => ({
        ...prev,
        waist: (parseFloat(params.waist) / 2.54).toFixed(1),
        hip: (parseFloat(params.hip) / 2.54).toFixed(1)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        waist: params.waist,
        hip: params.hip
      }));
    }
  };
  
  // Предопределенные возрастные группы
  const ageGroups = [
    { value: '20', label: '20-30' },
    { value: '35', label: '30-40' },
    { value: '45', label: '40-50' },
    { value: '60', label: '50+' }
  ];
  
  // Функция расчета
  const calculateWHR = () => {
    // Используем кнопку submit из формы
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <ActivitySquare className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Текущий WHR */}
        <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 text-center">
          <div className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">Текущий WHR</div>
          <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{currentWHR}</div>
        </div>
        
        {/* Выбор пола */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Пол</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleGenderChange('female')}
              className={`flex items-center justify-center p-3 rounded-lg border transition-colors
                ${formData.gender === 'female' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="flex flex-col items-center">
                <User className="w-5 h-5 mb-1" />
                <span>Женский</span>
              </div>
            </button>
            
            <button
              type="button"
              onClick={() => handleGenderChange('male')}
              className={`flex items-center justify-center p-3 rounded-lg border transition-colors
                ${formData.gender === 'male' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="flex flex-col items-center">
                <User className="w-5 h-5 mb-1" />
                <span>Мужской</span>
              </div>
            </button>
          </div>
        </div>
        
        {/* Обхват талии */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="quickWaist" className="text-gray-600 dark:text-gray-400 flex items-center">
              <Ruler className="w-4 h-4 mr-1" />
              Обхват талии
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.waist} {formData.measurementSystem === 'metric' ? 'см' : 'дюйм'}
            </span>
          </div>
          <input
            type="range"
            id="quickWaist"
            min={formData.measurementSystem === 'metric' ? '50' : '20'}
            max={formData.measurementSystem === 'metric' ? '150' : '60'}
            step="1"
            value={formData.waist}
            onChange={handleWaistChange}
            aria-label="Обхват талии"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
        </div>
        
        {/* Обхват бедер */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="quickHip" className="text-gray-600 dark:text-gray-400 flex items-center">
              <Ruler className="w-4 h-4 mr-1" />
              Обхват бедер
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.hip} {formData.measurementSystem === 'metric' ? 'см' : 'дюйм'}
            </span>
          </div>
          <input
            type="range"
            id="quickHip"
            min={formData.measurementSystem === 'metric' ? '70' : '30'}
            max={formData.measurementSystem === 'metric' ? '150' : '60'}
            step="1"
            value={formData.hip}
            onChange={handleHipChange}
            aria-label="Обхват бедер"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
        </div>
        
        {/* Типы телосложения */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип телосложения
          </div>
          <div className="grid grid-cols-2 gap-2">
            {presetBodyTypes.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleBodyTypeSelect(type.id)}
                className="p-2 text-xs rounded-lg border bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-300 transition-colors"
              >
                {type.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Возраст */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="quickAge" className="text-gray-600 dark:text-gray-400">
              Возраст
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.age} лет
            </span>
          </div>
          <input
            type="range"
            id="quickAge"
            min="16"
            max="90"
            step="1"
            value={formData.age}
            onChange={handleAgeChange}
            aria-label="Возраст"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с возрастными группами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {ageGroups.map(group => (
              <button
                key={group.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, age: group.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.age === group.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {group.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateWHR}
          icon={<ActivitySquare className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать WHR
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;